/**
 * The public offering simulator — pure arithmetic, no I/O.
 *
 * Split out of offerings.ts deliberately. This is the one piece of the public
 * marketplace that PRODUCES A FINANCIAL FIGURE rather than displaying one the
 * engine computed, so a silent error here is a misleading projection rather
 * than a broken layout. Keeping it free of imports means it can be exercised
 * directly by a runner with no browser, DOM, or network — see
 * scripts/verify-simulator.ts.
 */

import type { OfferingWithFinancials } from './offerings';

// ── The simulator ───────────────────────────────────────────────────────────

export interface SimulationInput {
  /** Hypothetical units sold per tier, keyed by tier name. */
  soldByTier: Record<string, number>;
  /** How many shares the visitor imagines holding. */
  shares: number;
}

export interface SimulationResult {
  grossTicketRevenueCents: number;
  voletoPlatformFeeCents: number;
  stripeProcessingFeeCents: number;
  /**
   * The venue's cut, where the event carries one. Zero otherwise, and reported as zero
   * rather than omitted so a caller never has to decide whether undefined means "no deal"
   * or "not computed". Deducted above the waterfall: the venue is an operating cost of
   * the location, not a participant in the split.
   */
  venueRevenueShareCents: number;
  /** 100% of this transfers to the SPV at settlement. */
  netProceedsCents: number;

  // The waterfall, in the order it is paid.
  /** Tier 1: capital returned to investors before anyone takes a profit share. */
  returnOfCapitalCents: number;
  /** Tier 2: the hurdle, also entirely to investors. */
  preferredReturnCents: number;
  /** Tier 3: only this is split. */
  profitAboveHurdleCents: number;
  investorPoolCents: number;
  /** The 40% carried interest, to the manager. Not to Voleto. */
  gpFeeCents: number;

  /** This visitor's slice, across all three tiers. */
  netPositionCents: number;
  investedCents: number;
  /** netPosition - invested. Negative is a real, expected outcome. */
  gainCents: number;
}

/**
 * Run the settlement waterfall over hypothetical ticket sales.
 *
 * Mirrors the engine's runSettlement arithmetic (engine src/services/settlement.js)
 * using the rates the engine hands us, rather than constants duplicated here — if
 * the platform fee ever changes, this simulator changes with it instead of quietly
 * projecting the old economics. The same rule is why the venue revenue share is read from
 * rates rather than assumed absent: settlementMath.js is the single source of truth, and a
 * simulator missing a line item the engine deducts overstates the return by exactly that
 * line.
 *
 * ONE DELIBERATE OMISSION, and it matters: expenses the raise did not already pay
 * (settlement costs, taxes, post-event) reduce the pool and are NOT public. This
 * projection cannot include them, so it is OPTIMISTIC by that amount. It is NOT
 * optimistic by the production budget: the raise funds that, and it comes back as
 * return of capital rather than being deducted from revenue. Every surface that
 * renders this result has to say so — see ProductionCostCaveat in the detail page.
 * Silently omitting a subtraction from a projected return is how a simulator
 * becomes a misleading statement.
 */
export function simulate(
  // Takes the NARROWED type on purpose: a projection cannot be computed for a viewer the
  // engine withheld figures from, and the compiler is what enforces that rather than a
  // convention somebody has to remember.
  offering: OfferingWithFinancials,
  { soldByTier, shares }: SimulationInput,
): SimulationResult {
  const { rates } = offering.simulator;

  const grossTicketRevenueCents = offering.ticketTiers.reduce((total, tier) => {
    const sold = Math.max(0, Math.min(soldByTier[tier.name] ?? 0, tier.quantityTotal));
    return total + sold * tier.priceCents;
  }, 0);

  const voletoPlatformFeeCents = Math.round(grossTicketRevenueCents * rates.voletoPlatformFeeRate);

  // Card processing has a fixed component charged per TRANSACTION, and the simulator has
  // no way to know how many transactions a given sell-through would arrive in: one buyer
  // taking four tickets pays it once, four buyers pay it four times. It assumes one order
  // per ticket, which is the most orders a sell-through can produce and therefore the
  // largest the charge can be. The result is a floor on net proceeds rather than a
  // flattering estimate, and the page says so.
  const assumedOrderCount = Object.entries(soldByTier).reduce((total, [name, sold]) => {
    const tier = offering.ticketTiers.find((t) => t.name === name);
    return total + (tier ? Math.max(0, Math.min(sold, tier.quantityTotal)) : 0);
  }, 0);
  const stripeProcessingFeeCents =
    Math.round(grossTicketRevenueCents * rates.stripeProcessingRate)
    + assumedOrderCount * rates.stripeFixedFeeCents;

  // The venue's negotiated cut, where the event carries one. Charged on GROSS, exactly
  // like the platform fee above it, and taken before net proceeds reach the SPV.
  //
  // Clamped to what is actually left, matching the engine's computeWaterfall. Flooring net
  // proceeds instead would keep showing a venue line the remaining money cannot cover, and
  // the projected figures on screen would stop summing to the gross above them.
  //
  // Zero for nearly every offering. This whole block is a no-op at a zero rate, which is
  // what keeps the projection for an ordinary event identical to what it was before.
  const venueRevenueShareCents = Math.min(
    Math.round(grossTicketRevenueCents * (rates.venueRevenueShareRate ?? 0)),
    Math.max(0, grossTicketRevenueCents - voletoPlatformFeeCents - stripeProcessingFeeCents),
  );

  // Net proceeds: 100% of this transfers to the SPV at settlement.
  const netProceedsCents =
    grossTicketRevenueCents - voletoPlatformFeeCents - stripeProcessingFeeCents
    - venueRevenueShareCents;

  // NET EVENT CASH GOES STRAIGHT INTO THE WATERFALL. The funded production budget is not
  // deducted here and is not deducted at settlement either: the raise IS that budget, and
  // return of capital below is where it is accounted for. Subtracting it as well charged
  // the same cost twice.
  //
  // What the simulator still cannot see is expenses the raise did NOT pay (settlement
  // costs, taxes, post-event), which are not public. Every figure below stays an upper
  // bound, by a smaller amount than before. The page says so beside the result.
  const distributable = Math.max(0, netProceedsCents);

  // The waterfall assumes a fully subscribed raise, since that is the only capital figure
  // a public visitor can know. A partially funded raise returns capital sooner and reaches
  // the promote later, so this is the conservative direction for the promote and the
  // optimistic one for the investor.
  const totalCapitalCents =
    offering.structure.totalShares * offering.structure.pricePerShareCents;

  // Tier 1. Nobody takes profit before principal is back.
  const returnOfCapitalCents = Math.min(distributable, totalCapitalCents);
  let remaining = distributable - returnOfCapitalCents;

  // Tier 2. The flat hurdle, also entirely to investors.
  //
  // The rate comes from the ENGINE, not from offering.structure.preferredReturnPercentage.
  // That column is a historical record of what individual contracts were written against
  // and no longer drives settlement, so reading it here would project a hurdle the engine
  // will never pay, on exactly the screen a prospective investor decides from.
  const hurdleRate = rates.preferredReturnRate;
  const preferredReturnCents = Math.min(remaining, Math.round(totalCapitalCents * hurdleRate));
  remaining -= preferredReturnCents;

  // Tier 3. Only the excess is split, 60/40. The GP side is the exact remainder so the
  // two halves sum back to the excess at every value, matching the engine cent for cent.
  const profitAboveHurdleCents = remaining;
  const investorPoolCents = Math.round(profitAboveHurdleCents * rates.investorSplitRate);
  const gpFeeCents = profitAboveHurdleCents - investorPoolCents;

  const heldShares = Math.max(0, shares);
  const investedCents = heldShares * offering.structure.pricePerShareCents;

  // Allocated by capital, matching the engine. With one price per unit inside a raise,
  // capital share and unit share are the same ratio.
  const capitalShare = totalCapitalCents > 0 ? investedCents / totalCapitalCents : 0;
  const netPositionCents = Math.round(
    (returnOfCapitalCents + preferredReturnCents + investorPoolCents) * capitalShare);

  return {
    grossTicketRevenueCents,
    voletoPlatformFeeCents,
    stripeProcessingFeeCents,
    venueRevenueShareCents,
    netProceedsCents,
    returnOfCapitalCents,
    preferredReturnCents,
    profitAboveHurdleCents,
    investorPoolCents,
    gpFeeCents,
    netPositionCents,
    investedCents,
    gainCents: netPositionCents - investedCents,
  };
}
